import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import HeroAnimation from '../components/landing/HeroAnimation';
import MagneticSynthesisAnimation from '../components/landing/MagneticSynthesisAnimation';
import FlashlightAnimation from '../components/landing/FlashlightAnimation';
import { signInWithGoogle, isNativePlatform } from '../utils/nativeAuth';

function LandingPage() {
  const navigate = useNavigate();
  const { user, refreshAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }

    const error = searchParams.get('error');
    if (error === 'auth_failed') {
      toast.error('ההתחברות נכשלה. נסה שוב.');
    }
  }, [user, navigate, searchParams]);

  const handleGoogleLogin = async () => {
    if (isNativePlatform()) {
      // Native mobile: Use native Google Sign-In
      setIsLoading(true);
      try {
        const result = await signInWithGoogle();
        if (result?.success) {
          // Refresh user context and navigate
          if (refreshAuth) {
            await refreshAuth();
          }
          navigate('/dashboard');
        }
      } catch (error) {
        console.error('[LandingPage] Native sign-in error:', error);
        toast.error('ההתחברות נכשלה. נסה שוב.');
      } finally {
        setIsLoading(false);
      }
    } else {
      // Web: Use OAuth redirect flow
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      window.location.href = `${backendUrl}/api/auth/google`;
    }
  };

  const GoogleIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );

  const painPoints = [
    {
      icon: "🏦",
      title: "מידע מפוזר",
      description: "מנהלים כמה חשבונות בנק או כרטיסים? כבר כואב לעבור בין 19 פירוטי אשראי שונים?",
      illustration: "/illustrations/banks.svg"
    },
    {
      icon: "💵",
      title: "מזומן נשכח",
      description: "הוצאות במזומן עפות ברוח. עם הקלטה קולית, תשלטו בהם כמו עפיפון.",
      illustration: "/illustrations/cash.svg"
    },
    {
      icon: "🏷️",
      title: "קטגוריות שגויות",
      description: "אמזון = שופינג בעיני הבנק, לא בהכרח. אנחנו יודעים שאפשר לקנות שם גם פיצוחים במשקל.",
      illustration: "/illustrations/categories.svg"
    },
    {
      icon: "📝",
      title: "יותר מדי חיכוך",
      description: "טפסים, שדות, תפריטים... התשה. פשוט דברו או הקלידו. זהו.",
      illustration: "/illustrations/friction.svg"
    }
  ];

  return (
    <div className="min-h-screen bg-background-light" dir="rtl">
      {/* Header */}
      <header className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-2xl font-black">
            <img src="/icon-192.png" alt="שקלות" className="w-8 h-8" />
            שקלות
          </div>
          <button
            onClick={handleGoogleLogin}
            disabled={isLoading}
            className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50"
          >
            <GoogleIcon />
            {isLoading ? 'מתחבר...' : 'כניסה'}
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 md:py-24">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-lg text-gray-600 mb-4">היי, אנחנו שקלות 👋</p>
            <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight">
              מעקב הוצאות
              <br />
              בשפה חופשית, בחינם!
            </h1>
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              עוקבים אחרי כל ההוצאות במקום אחד.
              <br />
              בלי סיבוכים - פשוט מדברים או מקלידים.
            </p>
            {/* <button
              onClick={handleGoogleLogin}
              className="btn-primary text-lg inline-flex items-center gap-3"
            >
              <GoogleIcon />
              התחילו בחינם
            </button> */}
          </div>
          
          {/* Hero Animation */}
          <div className="relative">
            <HeroAnimation />
            {/* Decorative elements */}
            <div className="absolute -top-4 -left-4 w-16 h-16 bg-primary rounded-full border-4 border-black"></div>
            <div className="absolute -bottom-4 -right-4 w-12 h-12 bg-accent rounded-lg border-4 border-black transform rotate-12"></div>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl md:text-5xl font-black text-center mb-16">
          מה אנחנו פותרים
        </h2>
        
        <div className="space-y-24">
          {painPoints.map((point, index) => (
            <div 
              key={index}
              className={`grid md:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'md:flex-row-reverse' : ''
              }`}
            >
              {/* Content */}
              <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                <div className="inline-block bg-primary/20 rounded-2xl p-4 mb-6">
                  <span className="text-4xl">{point.icon}</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-black mb-4">
                  {point.title}
                </h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  {point.description}
                </p>
              </div>
              
              {/* Illustration or Demo */}
              <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                {point.title === 'מידע מפוזר' ? (
                  <MagneticSynthesisAnimation />
                ) : point.title === 'מזומן נשכח' ? (
                  <FlashlightAnimation />
                ) : point.title === 'קטגוריות שגויות' ? (
                  <div className="rounded-2xl border-4 border-black overflow-hidden shadow-hard">
                    <img 
                      src="/missed_categories_illustration.png" 
                      alt="קטגוריות שגויות" 
                      className="w-full h-auto"
                    />
                  </div>
                ) : point.title === 'יותר מדי חיכוך' ? (
                  <div className="rounded-2xl border-4 border-black overflow-hidden shadow-hard">
                    <img 
                      src="/low_friction_illustration.png" 
                      alt="יותר מדי חיכוך" 
                      className="w-full h-auto"
                    />
                  </div>
                ) : (
                  <div className="bg-gray-100 rounded-2xl border-2 border-black p-8 aspect-video flex items-center justify-center">
                    <div className="text-center text-gray-400">
                      <div className="text-4xl mb-2">🖼️</div>
                      <p className="font-bold text-sm">איור: {point.title}</p>
                      <p className="text-xs">600x400px</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works Section */}
      <section className="container mx-auto px-4 py-16">
        <h1 className="text-4xl md:text-5xl font-black text-center mb-12 uppercase leading-[0.9] tracking-tighter">
          איך זה <span className="bg-green-400 px-2 text-black inline-block transform -rotate-2 border-2 border-black shadow-hard-sm">עובד</span>?
        </h1>
        
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row md:justify-between md:items-start gap-12 md:gap-8">
          {/* Step 1 */}
          <div className="flex flex-col items-center text-center group flex-1">
            <div className="relative mb-6">
              <div className="absolute -top-6 -right-4 text-4xl animate-bounce z-20">
                🎤
              </div>
              <div className="w-20 h-20 rounded-full bg-primary border-[3px] border-black flex items-center justify-center text-4xl font-black text-black shadow-hard z-10 relative transition-transform group-hover:translate-x-[2px] group-hover:translate-y-[2px] group-hover:shadow-none">
                1
              </div>
            </div>
            <h2 className="text-2xl font-black mb-2 flex items-center gap-2">
              דברו או הקלידו
            </h2>
            <div className="bg-white border-2 border-black p-3 rounded-lg shadow-hard-sm max-w-[280px] w-full transform rotate-1">
              <p className="font-mono text-sm text-gray-600 font-bold">
                "שילמתי 50 שקל על פיצה במזומן"
              </p>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex flex-col items-center text-center group flex-1">
            <div className="relative mb-6">
              <div className="absolute -top-6 -right-4 text-3xl animate-pulse z-20">
                🤖
              </div>
              <div className="w-20 h-20 rounded-full bg-accent border-[3px] border-black flex items-center justify-center text-4xl font-black text-black shadow-hard z-10 relative transition-transform group-hover:translate-x-[2px] group-hover:translate-y-[2px] group-hover:shadow-none">
                2
              </div>
            </div>
            <h2 className="text-2xl font-black mb-2">
              AI מעבד
            </h2>
            <p className="text-base font-medium opacity-80 max-w-[250px] leading-relaxed">
              מזהה סכום, מטבע, קטגוריה ואמצעי תשלום באופן אוטומטי
            </p>
          </div>

          {/* Step 3 */}
          <div className="flex flex-col items-center text-center group flex-1">
            <div className="relative mb-6">
              <div className="absolute -top-6 -right-4 text-3xl z-20">
                ✅
              </div>
              <div className="w-20 h-20 rounded-full bg-green-400 border-[3px] border-black flex items-center justify-center text-4xl font-black text-black shadow-hard z-10 relative transition-transform group-hover:translate-x-[2px] group-hover:translate-y-[2px] group-hover:shadow-none">
                3
              </div>
            </div>
            <h2 className="text-2xl font-black mb-2">
              הוצאה נשמרה!
            </h2>
            <p className="text-base font-medium opacity-80 max-w-[250px] leading-relaxed">
              ההוצאה נשמרה, מקוטלגת ומוכנה לצפייה בדוחות
            </p>
            {/* Receipt with scalloped/perforated top edge */}
            <div className="mt-6 w-full max-w-[220px] shadow-hard" dir="rtl">
              {/* Scalloped top edge */}
              <div className="relative">
                <svg className="w-full h-4 block" viewBox="0 0 220 16" preserveAspectRatio="none">
                  {/* White fill for the scallops */}
                  <path 
                    d="M0,16 L0,8 Q5,16 10,8 Q15,16 20,8 Q25,16 30,8 Q35,16 40,8 Q45,16 50,8 Q55,16 60,8 Q65,16 70,8 Q75,16 80,8 Q85,16 90,8 Q95,16 100,8 Q105,16 110,8 Q115,16 120,8 Q125,16 130,8 Q135,16 140,8 Q145,16 150,8 Q155,16 160,8 Q165,16 170,8 Q175,16 180,8 Q185,16 190,8 Q195,16 200,8 Q205,16 210,8 Q215,16 220,8 L220,16 Z" 
                    fill="white"
                  />
                  {/* Black stroke for the scalloped edge only */}
                  <path 
                    d="M0,8 Q5,16 10,8 Q15,16 20,8 Q25,16 30,8 Q35,16 40,8 Q45,16 50,8 Q55,16 60,8 Q65,16 70,8 Q75,16 80,8 Q85,16 90,8 Q95,16 100,8 Q105,16 110,8 Q115,16 120,8 Q125,16 130,8 Q135,16 140,8 Q145,16 150,8 Q155,16 160,8 Q165,16 170,8 Q175,16 180,8 Q185,16 190,8 Q195,16 200,8 Q205,16 210,8 Q215,16 220,8" 
                    fill="none"
                    stroke="black"
                    strokeWidth="2"
                  />
                  {/* Left and right edges */}
                  <line x1="1" y1="8" x2="1" y2="16" stroke="black" strokeWidth="2"/>
                  <line x1="219" y1="8" x2="219" y2="16" stroke="black" strokeWidth="2"/>
                </svg>
              </div>
              {/* Receipt body */}
              <div className="bg-white border-x-2 border-b-2 border-black p-3 rounded-b-lg -mt-[1px]">
                <div className="flex items-center justify-between border-b-2 border-dashed border-gray-300 pb-2 mb-2">
                  <span className="font-bold text-sm">פיצה</span>
                  <span className="text-2xl">🍕</span>
                </div>
                <div className="space-y-1 text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span className="font-medium">אמצעי תשלום:</span>
                    <span>מזומן</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">קטגוריה:</span>
                    <span>אוכל</span>
                  </div>
                </div>
                <div className="mt-3 pt-2 border-t-2 border-dashed border-gray-300 flex justify-between items-center">
                  <span className="font-medium">סכום:</span>
                  <span className="font-black text-l font-mono">₪50.00</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="container mx-auto px-4 py-16">
        <div className="bg-primary/10 rounded-3xl border-4 border-black p-12 text-center">
          <h2 className="text-3xl md:text-4xl font-black mb-6">
            מוכנים לקחת שליטה על ההוצאות?
          </h2>
          <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
            הצטרפו לשקלות היום. זה בחינם, מהיר, ובאמת נעים לשימוש.
          </p>
          <button
            onClick={handleGoogleLogin}
            disabled={isLoading}
            className="btn-primary text-xl inline-flex items-center gap-3 disabled:opacity-50"
          >
            <GoogleIcon />
            {isLoading ? 'מתחבר...' : 'הצטרפו חינם עם גוגל'}
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t-4 border-black mt-16">
        <div className="container mx-auto px-4 py-8 text-center">
          <p className="font-bold text-lg">נבנה עם 💚 בשביל אנשים שלא אוהבים לעקוב אחרי הוצאות</p>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
